import React from "react";
import { Home as HomeIcon, Building2, Building, Sun, HeartHandshake, Check, MapPin, Clock, Sparkles, Users2, ArrowRight, Tag, Coffee } from "lucide-react";

const HOURLY = [
    { icon: HomeIcon, name: "Wohnungsreinigung", price: "CHF 39", unit: "/ Stunde" },
    { icon: Sun, name: "Fensterreinigung", price: "CHF 43", unit: "/ Stunde" },
    { icon: HeartHandshake, name: "Begleitservice / Alltagshilfe", price: "CHF 36", unit: "/ Stunde" },
];

const TRAVEL = [
    { region: "Wil SG & Umgebung", price: "CHF 20" },
    { region: "Ostschweiz / Zürcher Oberland", price: "CHF 30" },
    { region: "Weitere Regionen", price: "auf Anfrage" },
];

const EXTRAS = [
    { name: "Kühlschrank innen", price: "CHF 25" },
    { name: "Backofenreinigung", price: "CHF 35" },
    { name: "Balkon", price: "ab CHF 20" },
    { name: "Stark verschmutzte Objekte", price: "Zuschlag" },
];

const INCLUDED_WINDOWS = [
    "Fenster innen & aussen",
    "Rahmenreinigung",
    "Fenstersims",
    "Professionelles Reinigungsmaterial",
    "Streifenfreie Reinigung",
];

const INCLUDED_HOME = [
    "Küche reinigen",
    "Badezimmer / WC reinigen",
    "Staubsaugen & Bodenreinigung",
    "Oberflächen reinigen",
    "Allgemeine Unterhaltsreinigung",
];

const INCLUDED_COMPANION = [
    "Einkaufshilfe",
    "Begleitung zu Terminen",
    "Unterstützung zuhause",
    "Gesellschaft & Alltagshilfe",
];

function CheckList({ items, color = "#16A34A", testId }) {
    return (
        <ul className="space-y-2.5" data-testid={testId}>
            {items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-[#0F172A]">
                    <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-[#F0FDF4] items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#16A34A]" strokeWidth={3} />
                    </span>
                    {item}
                </li>
            ))}
        </ul>
    );
}

export default function PricingSection() {
    return (
        <section id="prices" className="py-24 lg:py-32" data-testid="pricing-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="max-w-3xl mb-14">
                    <span className="overline mb-4 inline-block">Preise & Aktionen</span>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A365D] tracking-tight">
                        Einführungspreise <span className="text-[#16A34A]">2026</span>
                    </h2>
                    <p className="mt-5 text-lg text-[#64748B]">
                        Professionelle Dienstleistungen zu fairen Einführungspreisen. Transparent, zuverlässig und unkompliziert — Region Ostschweiz / Zürcher Oberland.
                    </p>
                </div>

                {/* Hourly rates cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                    {HOURLY.map((h) => (
                        <div key={h.name} className="rounded-2xl border border-slate-200 bg-white p-7 hover:-translate-y-1 transition-transform shadow-sm" data-testid={`price-card-${h.name.toLowerCase().replace(/\s+/g, "-")}`}>
                            <div className="inline-flex w-11 h-11 rounded-xl bg-[#F0FDF4] text-[#16A34A] items-center justify-center mb-4">
                                <h.icon className="w-5 h-5" />
                            </div>
                            <h3 className="font-display font-bold text-lg text-[#1A365D] mb-2">{h.name}</h3>
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-display font-black text-3xl text-[#16A34A]">{h.price}</span>
                                <span className="text-sm text-[#64748B] font-medium">{h.unit}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Two info badges */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
                    <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-6 flex items-start gap-4" data-testid="min-order-card">
                        <div className="w-11 h-11 rounded-xl bg-[#1A365D] text-white flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider font-bold text-[#16A34A]">Mindestauftrag</div>
                            <div className="font-display font-bold text-xl text-[#1A365D] mt-1">2 Stunden</div>
                            <p className="text-sm text-[#64748B] mt-1">Wir sind transparent — ohne versteckte Mindestpauschalen.</p>
                        </div>
                    </div>
                    <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-6" data-testid="travel-card">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-11 h-11 rounded-xl bg-[#1A365D] text-white flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5" />
                            </div>
                            <div className="text-xs uppercase tracking-wider font-bold text-[#16A34A]">Anfahrtspauschale</div>
                        </div>
                        <ul className="divide-y divide-slate-200">
                            {TRAVEL.map((t) => (
                                <li key={t.region} className="flex items-center justify-between py-2 text-sm">
                                    <span className="text-[#0F172A]">{t.region}</span>
                                    <span className="font-semibold text-[#1A365D]">{t.price}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* What's included — 3 service cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-16">
                    <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm" data-testid="inclusions-windows">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A365D] text-white text-xs font-bold uppercase tracking-wider mb-5">
                            <Sun className="w-3.5 h-3.5" /> Fenster & Schaufenster
                        </div>
                        <h3 className="font-display font-bold text-xl text-[#1A365D] mb-2">Inbegriffen</h3>
                        <CheckList items={INCLUDED_WINDOWS} testId="windows-checklist" />
                        <div className="mt-5 pt-5 border-t border-slate-200">
                            <div className="text-xs uppercase tracking-wider font-bold text-[#16A34A] mb-2">Geeignet für</div>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="font-bold text-[#1A365D] mb-1">Privat</div>
                                    <div className="text-[#64748B] leading-relaxed">Wohnungen · Häuser · Wintergärten · Rollläden</div>
                                </div>
                                <div>
                                    <div className="font-bold text-[#1A365D] mb-1">Geschäft</div>
                                    <div className="text-[#64748B] leading-relaxed">Schaufenster · Glasfronten · Büros · Eingangsbereiche</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm" data-testid="inclusions-home">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16A34A] text-white text-xs font-bold uppercase tracking-wider mb-5">
                            <HomeIcon className="w-3.5 h-3.5" /> Wohnungsreinigung
                        </div>
                        <h3 className="font-display font-bold text-xl text-[#1A365D] mb-2">Inbegriffen</h3>
                        <CheckList items={INCLUDED_HOME} testId="home-checklist" />
                        <div className="mt-5 pt-5 border-t border-slate-200">
                            <div className="text-xs uppercase tracking-wider font-bold text-[#16A34A] mb-3">Zusatzleistungen</div>
                            <ul className="divide-y divide-slate-200">
                                {EXTRAS.map((e) => (
                                    <li key={e.name} className="flex items-center justify-between py-1.5 text-xs">
                                        <span className="text-[#0F172A]">{e.name}</span>
                                        <span className="font-semibold text-[#1A365D]">{e.price}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm" data-testid="inclusions-companion">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A365D] text-white text-xs font-bold uppercase tracking-wider mb-5">
                            <HeartHandshake className="w-3.5 h-3.5" /> Begleitservice
                        </div>
                        <h3 className="font-display font-bold text-xl text-[#1A365D] mb-2">Unterstützung im Alltag</h3>
                        <CheckList items={INCLUDED_COMPANION} testId="companion-checklist" />
                        <div className="mt-5 pt-5 border-t border-slate-200 text-xs text-[#64748B] leading-relaxed">
                            <Coffee className="w-4 h-4 inline mr-1.5 text-[#16A34A] -mt-0.5" />
                            Persönlich, freundlich und mit Herz — wir sind da, wenn Sie Hilfe brauchen.
                        </div>
                    </div>
                </div>

                {/* Neighborhood discount */}
                <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#F0FDF4] to-white border border-[#16A34A]/20 p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="neighborhood-discount">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16A34A] text-white text-xs font-bold uppercase tracking-wider mb-4">
                            <Users2 className="w-3.5 h-3.5" /> Nachbarschafts-Vorteil
                        </div>
                        <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#1A365D] tracking-tight mb-3">
                            Rabatt bei Sammelterminen
                        </h3>
                        <p className="text-[#64748B] max-w-2xl mb-5">
                            Werden mehrere Kunden in derselben Strasse oder Nachbarschaft am gleichen Tag bedient, profitieren alle.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div className="inline-flex items-center gap-2 text-[#0F172A]"><Check className="w-4 h-4 text-[#16A34A]" strokeWidth={3} /> Reduzierte Anfahrt</div>
                            <div className="inline-flex items-center gap-2 text-[#0F172A]"><Check className="w-4 h-4 text-[#16A34A]" strokeWidth={3} /> Spezialkonditionen</div>
                            <div className="inline-flex items-center gap-2 text-[#0F172A]"><Check className="w-4 h-4 text-[#16A34A]" strokeWidth={3} /> Effizientere Einsätze</div>
                        </div>
                    </div>
                    <a href="#booking" className="btn-primary self-end md:self-center" data-testid="pricing-cta-book">
                        Jetzt anfragen <ArrowRight className="w-4 h-4" />
                    </a>
                </div>

                {/* Footnote */}
                <p className="mt-8 text-xs text-[#64748B] text-center" data-testid="pricing-footnote">
                    <Tag className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    Alle Preise verstehen sich exkl. MwSt. — Endpreis je nach Aufwand und Region. Für eine schnelle Richtofferte: Fotos, Wohnungsgrösse, Anzahl Fenster und Region per WhatsApp senden.
                </p>
            </div>
        </section>
    );
}
